import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Icon, Nullable, theme } from "uikit";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import softphoneWidget, {
	Call as CallType,
} from "../../../../../../../../../redux/reducers/softphoneWidget";
import { RegisteredIcon } from "../../../../../../../../../assets/icons";
import {
	getAndCallDown,
	callUp,
} from "../../../../../../../../../utils/jsSip";
import getCallTypes from "../../../../../../../../../components/SoftphoneWidget/constants";
import { KeyBind } from "../../../../../../../../../components";
import {
	StyledColumn,
	StyledRow,
} from "../../../../../../../../../components/common";

const CallStyled = styled(StyledRow)`
	padding: 8px;
	height: 52px;
	width: 360px;

	display: flex;
	justify-content: space-between;
	align-items: center;

	border-radius: 12px;
	background-color: ${theme.colors.white};
	box-shadow: 0px 6px 16px 0px #00000026;

	cursor: pointer;
	position: relative;
`;

const Avatar = styled("div")<{ bgColor: string; fontColor: string }>`
	height: 36px;
	width: 36px;

	display: flex;
	justify-content: center;
	align-items: center;

	color: ${({ fontColor }) => fontColor};
	font-family: Lato;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	letter-spacing: 0em;
	text-align: center;

	border-radius: 50%;
	background-color: ${({ bgColor }) => bgColor};
	cursor: pointer;
`;

const PhoneNumber = styled(StyledRow)`
	color: ${theme.colors.primary};
	font-family: Lato;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0px;
	text-align: left;
	cursor: pointer;

	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const CallerName = styled(StyledRow)`
	color: ${theme.colors.secondary};
	font-family: Lato;
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0px;
	text-align: left;
	cursor: pointer;

	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Duration = styled("span")<{ color: string }>`
	color: ${({ color }) => color || theme.colors.primary};
	font-family: Lato;
	font-size: 12px;
	font-weight: 500;
	line-height: 15px;
	letter-spacing: 0em;
	text-align: center;
	cursor: pointer;
`;

const MetaColumn = styled(StyledColumn)<{ visible: boolean }>`
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
	pointer-events: none;
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
`;

const ActionButtons = styled(StyledRow)<{ visible: boolean }>`
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
	pointer-events: ${({ visible }) => (visible ? "auto" : "none")};
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	z-index: 2;
`;

const ActionButton = styled(Button.Button)`
	height: 40px !important;
	width: 40px !important;
	min-width: 40px !important;
	border-radius: 50% !important;
	padding: 0 !important;
`;

interface CallTypeAndDurationProps {
	color: string;
	iconName: RegisteredIcon;
	duration: string | null;
}

const CallTypeAndDuration: React.FC<CallTypeAndDurationProps> = ({
	color,
	iconName,
	duration,
}) => (
	<StyledRow style={{ height: 16 }} alignItems="center" gap="4px">
		<Icon id={iconName} size={16} colors={[color]} />
		<Duration color={color}>{duration}</Duration>
	</StyledRow>
);

interface Props {
	data: CallType;
	selectedCall: Nullable<CallType>;
	callerIndex: number;
	onSelectCaller: (callerData: CallType) => void;
}

const Call: React.FC<Props> = ({
	data,
	selectedCall,
	callerIndex,
	onSelectCaller,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const callTypes = useMemo(() => getCallTypes(t), [t]);

	const [isHovered, setIsHovered] = useState(false);
	const callPausedBgColor = useMemo(
		() =>
			selectedCall?.session._localHold &&
			selectedCall?.session.id === data?.session.id
				? callTypes.pause.primaryColor
				: callTypes.pause.secondaryColor,
		[
			callTypes.pause.primaryColor,
			callTypes.pause.secondaryColor,
			data?.session.id,
			selectedCall?.session._localHold,
			selectedCall?.session.id,
		],
	);

	const callPausedFontColor = useMemo(
		() =>
			selectedCall?.session._localHold &&
			selectedCall?.session.id === data?.session.id
				? theme.colors.white
				: callTypes.pause.primaryColor,
		[
			callTypes.pause.primaryColor,
			data?.session.id,
			selectedCall?.session._localHold,
			selectedCall?.session.id,
		],
	);

	const callBgColor = useMemo(() => {
		if (data?.session._is_confirmed) {
			if (selectedCall?.session.id === data?.session.id) {
				return callTypes.current.primaryColor;
			}
			return callTypes.current.secondaryColor;
		}
		return selectedCall?.session.id === data?.session.id
			? callTypes[data.session.direction].primaryColor
			: callTypes[data.session.direction].secondaryColor;
	}, [
		callTypes,
		data.session._is_confirmed,
		data.session.direction,
		data.session.id,
		selectedCall?.session.id,
	]);
	const callFontColor = useMemo(() => {
		selectedCall?.session.id === data?.session.id
			? theme.colors.white
			: callTypes[data.session.direction].primaryColor;

		if (data?.session._is_confirmed) {
			if (selectedCall?.session.id === data?.session.id) {
				return theme.colors.white;
			}
			return callTypes.current.primaryColor;
		}
		return selectedCall?.session.id === data?.session.id
			? theme.colors.white
			: callTypes[data.session.direction].primaryColor;
	}, [
		callTypes,
		data.session._is_confirmed,
		data.session.direction,
		data.session.id,
		selectedCall?.session.id,
	]);

	const bgColor = useMemo(
		() => (data?.session._localHold ? callPausedBgColor : callBgColor),
		[callBgColor, callPausedBgColor, data?.session._localHold],
	);

	const fontColor = useMemo(
		() => (data?.session._localHold ? callPausedFontColor : callFontColor),
		[callFontColor, callPausedFontColor, data?.session._localHold],
	);

	const selectCaller = useCallback(() => {
		onSelectCaller(data);
	}, [data, onSelectCaller]);

	const showCallButtons = useCallback(() => {
		setIsHovered(true);
	}, []);
	const hideCallButton = useCallback(() => {
		setIsHovered(false);
	}, []);

	const durationColor = useMemo(() => {
		if (data.session._localHold) {
			return callTypes[data.session.direction].primaryColor;
		}
		if (data.session._is_confirmed) {
			return callTypes.current.primaryColor;
		}
		return callTypes[data.session.direction].primaryColor;
	}, [
		callTypes,
		data.session._is_confirmed,
		data.session._localHold,
		data.session.direction,
	]);

	const durationIcon = useMemo(() => {
		if (data.session._localHold) {
			return callTypes[data.session.direction].iconName;
		}
		if (data.session._is_confirmed) {
			return callTypes.current.iconName;
		}
		return callTypes[data.session.direction].iconName;
	}, [
		callTypes,
		data.session._is_confirmed,
		data.session._localHold,
		data.session.direction,
	]);

	const callDown = useCallback(
		(event?: React.MouseEvent) => {
			event?.stopPropagation();
			getAndCallDown(data);
		},
		[data],
	);

	const callButtonOnClick = useCallback(
		(event?: React.MouseEvent) => {
			event?.stopPropagation();
			callUp(false, data);
		},
		[data],
	);

	const holdButtonOnClick = useCallback(
		(event?: React.MouseEvent) => {
			event?.stopPropagation();
			dispatch(softphoneWidget.actions.setSelectedCaller(data));
			if (data.session._localHold) {
				data.session.unhold();
			} else {
				data.session.hold();
			}
			dispatch(softphoneWidget.actions.rerenderSoftphone());
		},
		[data, dispatch],
	);

	const holdIconColor = useMemo(() => {
		if (!data.session._is_confirmed) {
			return theme.colors.disabled_text;
		}
		if (data.session._localHold) {
			return theme.colors.accent;
		}
		return theme.colors.secondary;
	}, [data.session._is_confirmed, data.session._localHold]);

	const holdBackgroundColor = data.session._localHold
		? "#E3F1F7"
		: "#F5F5F5";

	return (
		<CallStyled
			onClick={selectCaller}
			onMouseEnter={showCallButtons}
			onMouseLeave={hideCallButton}
		>
			<KeyBind settingId="callUpSoftphone" onUse={() => callUp(true)} />
			<KeyBind
				settingId="callDownSoftphone"
				onUse={() => getAndCallDown()}
			/>
			<StyledRow gap="8px" style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
				<Avatar bgColor={bgColor} fontColor={fontColor}>
					{callerIndex}
				</Avatar>
				<StyledColumn justify="center" style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
					<PhoneNumber>
						{data.session.remote_identity.uri.user}
					</PhoneNumber>
					{data.session?.name && (
						<CallerName>{data.session?.name}</CallerName>
					)}
				</StyledColumn>
			</StyledRow>
			<MetaColumn gap="2px" visible={!isHovered}>
				<CallTypeAndDuration
					color={durationColor}
					iconName={durationIcon}
					duration={data.callDuration}
				/>
				{data.session._localHold && (
					<CallTypeAndDuration
						color={callTypes.pause.primaryColor}
						iconName={callTypes.pause.iconName}
						duration={data.holdDuration}
					/>
				)}
			</MetaColumn>
			<ActionButtons gap="8px" visible={isHovered}>
				<ActionButton
					onClick={holdButtonOnClick}
					style={{
						backgroundColor: holdBackgroundColor,
					}}
					disabled={!data.session._is_confirmed}
					icon={
						<Icon
							id="phone-pause"
							size={20}
							colors={[holdIconColor]}
						/>
					}
				/>
				<ActionButton
					onClick={callButtonOnClick}
					style={{
						backgroundColor: "#4CAF50",
					}}
					disabled={
						data.session._is_confirmed ||
						data.session._direction === "outgoing"
					}
					icon={<Icon id="call-start" size={20} />}
				/>
				<ActionButton
					onClick={callDown}
					style={{
						backgroundColor: theme.colors.negative,
					}}
					icon={<Icon id="call-end" size={20} />}
				/>
			</ActionButtons>
		</CallStyled>
	);
};

export default Call;
